/*
 *  -----------------------------------------------------------------------
 *    <copyright file="generate-report.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { orderBy } from '@progress/kendo-data-query';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useTranslation } from 'hooks';
import { GET_DATASETS } from 'queries/central-monitoring';
import { ReportSignatoryFields, DataSetFields } from 'queries/generated/fragments.generated';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import ReportGrid from 'views/components/central-monitoring/reports/report-grid';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import Loading from 'views/components/core/loading';
import SelectWithCategories from 'views/components/core/select-with-categories';
import './styles.scss';

const GenerateReport = ({
    signatories,
    open,
    onClose,
    studyId,
}: {
    signatories: ReportSignatoryFields[];
    open: boolean;
    onClose: Function;
    studyId: string;
}) => {
    const { t } = useTranslation();

    const [getDataSets, { data, loading }] = useLazyQuery(GET_DATASETS);

    const [datasetOptions, setDatasetOptions] = useState([]);

    const [state, setState] = useState({
        datasetDate: datasetOptions[0]?.id ?? '',
        includeOnlyReviewedSites: false,
    });

    useEffect(() => {
        if (data?.dataSets) {
            const array = [...data.dataSets].filter((item: DataSetFields) => item.active);
            setDatasetOptions(
                orderBy(array, [{ field: 'dataCutDate', dir: 'desc' }]).map((item: any) => ({
                    id: item.id,
                    text: formatDateUTC_DEPRECATED(item.dataCutDate, true, 'DD-MMM-YYYY'),
                })) ?? [],
            );
        }
    }, [data?.dataSets]);

    useEffect(() => {
        if (open && studyId)
            getDataSets({
                variables: {
                    studyId,
                },
            });
    }, [open, studyId]);

    useEffect(() => {
        setState({
            ...state,
            datasetDate: datasetOptions[0]?.id ?? '',
        });
    }, [datasetOptions]);

    const submit = () => {
        setState({
            datasetDate: datasetOptions[0]?.id ?? '',
            includeOnlyReviewedSites: false,
        });
        const selectedDataset = data?.dataSets.find((item: any) => item.id === state.datasetDate);
        onClose('open-preview', {
            dataset: {
                ...selectedDataset,
            },
            includeOnlyReviewedSites: state.includeOnlyReviewedSites,
        });
    };

    const signatoriesData =
        signatories?.map((item: any) => ({
            ...item,
            name: `${item.firstName || ''} ${item.lastName || ''}`,
        })) ?? [];

    const close = () => {
        onClose();
    };

    if (loading) return <Loading float />;

    return (
        <CustomDialog onClose={close} aria-labelledby="add-risk" fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="generate-report-title">
                {t('cm:reportInclusions')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="add-risk-dialog-content remove-inactive-cv">
                <div className="sites-wrapper">
                    <p>{t('cm:includeSitesReview')}</p>
                    <Checkbox
                        rounded={null}
                        className="cell-checkbox no-full-width flex-start no-border"
                        checked={state.includeOnlyReviewedSites}
                        onChange={() => {
                            setState({
                                ...state,
                                includeOnlyReviewedSites: !state.includeOnlyReviewedSites,
                            });
                        }}
                        data-testid="include-only-reviewed-sites"
                        size="medium"
                    />
                </div>
                <FormControl variant="outlined" className="type-edc-wrapper" style={{ marginBottom: 15 }}>
                    <SelectWithCategories
                        label={t('cm:dataset')}
                        onChange={() => (event: any) => {
                            setState({
                                ...state,
                                datasetDate: event.target.value,
                            });
                        }}
                        dataArray={datasetOptions}
                        labelField="datasetDate"
                        fieldSelect="datasetDate"
                        customClassName="form-control-type not-full"
                        showWarning={false}
                        currentState={state.datasetDate}
                        customId="dataset-date"
                        filterable={false}
                    />
                </FormControl>
                <div className="list-info" style={{ marginBottom: 15 }}>
                    <p className="text-length">
                        {t(signatories?.length === 1 ? 'reports:singleSignatoryRecord' : 'reports:signatoryRecords', {
                            length: signatories?.length,
                        })}
                    </p>
                </div>
                <ReportGrid signatoriesData={signatoriesData} height={250} />
            </DialogContent>
            <DialogActions className="actions-info-wrapper-edit-cv actions-add-risk assign-items flex-end">
                <Button className="add-signatory-btn" text="view" onClick={submit} id="generate-report-btn" />
            </DialogActions>
        </CustomDialog>
    );
};

export default GenerateReport;
